import { AfterViewInit, Component, ElementRef, Input, input, SecurityContext, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Attachment } from '../../../../model/Attachment';
import { Email as EmailClass } from '../../../../model/Email';
import { IAttachment } from '../../../../model/attachment.model';
import { ClassifierMessage, Email } from '../../../../model/mail-classifier/classifier-message.model';
import { MailReviewService } from '../../../../services/mail-review.service';
import { MessageAttachmentService } from '../../../../services/message-attachment.service';
import { SelectedMailService } from '../../../../services/selected-mail.service';

@Component({
  selector: 'app-mail-classifier-details-body',
  templateUrl: './mail-classifier-details-body.component.html',
  styleUrls: ['./mail-classifier-details-body.component.scss'],
  standalone: false,
})
export class MailClassifierDetailsBodyComponent implements AfterViewInit {
  @ViewChild('iframe') iframe: ElementRef;
  @ViewChild('attachments') toolbarRef!: ElementRef;
  @ViewChild('iframeContainer') iframeContainerRef!: ElementRef;
  @Input() elementsToCalculateHeight: string[] = [];
  htmlData: any;
  currentEmail: EmailClass;
  screenHeight: number;
  screenWidth: number;
  loading = false;

  constructor(
    private sanitizer: DomSanitizer,
    private mailReviewService: MailReviewService,
    private selectedMailService: SelectedMailService,
    public messageAttachmentService: MessageAttachmentService,
  ) {}

  async ngAfterViewInit() {
    this.mailReviewService.currentClassifierMessage$.subscribe(async (message: ClassifierMessage) => {
      this.mailReviewService.setNavigationButtonsDisable(true);
      this.htmlData = null;
      if (message?.email) {
        await this.setCurrentEmail(message?.email);
        this.mailReviewService.setNavigationButtonsDisable(false);
      }
    });

    this.adjustIframeHeight();

    const resizeObserver = new ResizeObserver(() => {
      this.adjustIframeHeight();
    });
    resizeObserver.observe(this.toolbarRef.nativeElement);

    window.addEventListener('resize', () => {
      this.adjustIframeHeight();
    });
  }

  adjustIframeHeight() {
    const viewportHeight = window.innerHeight;
    let totalExtraHeight = 0;

    const defaultIds = ['top-navigation', 'attachments-container'];

    for (const id of defaultIds) {
      const el = document.getElementById(id);
      if (el) {
        totalExtraHeight += el.offsetHeight;
      }
    }

    for (const id of this.elementsToCalculateHeight) {
      const el = document.getElementById(id);
      if (el) {
        totalExtraHeight += el.offsetHeight;
      }
    }

    const newMaxHeight = viewportHeight - totalExtraHeight;
    this.iframeContainerRef.nativeElement.style.maxHeight = `${newMaxHeight}px`;
  }

  updateIframe() {
    if (this.currentEmail?.bodyHtml || this.currentEmail?.bodyPlain) {
      if (this.currentEmail?.bodyHtml) {
        this.removeDocType();
        this.htmlData = this.sanitizer.sanitize(
          SecurityContext.HTML,
          this.sanitizer.bypassSecurityTrustHtml(this.currentEmail.bodyHtml),
        );
      } else {
        this.htmlData = this.currentEmail?.bodyPlain;
      }

      setTimeout(() => {
        this.setIframeReady(this.iframe, this.htmlData);
      });
    } else {
      this.iframe.nativeElement.style.setProperty('display', 'none');
    }

    const element: HTMLIFrameElement = document.getElementById('emailIframe') as HTMLIFrameElement;
    const iframe = element?.contentWindow;
    iframe?.document?.querySelectorAll('*')?.forEach((n) => n.scrollTo(0, 0));
  }

  async setCurrentEmail(email: Email) {
    const transformedAttachments = email.attachments.map((attachment: IAttachment) => {
      return new Attachment({
        filename: attachment.name,
        key: attachment.path,
        isSafe: attachment.isSafe,
        fileId: 'TODO discard',
        size: attachment.size,
        isLocal: false,
        contentType: attachment.contentType,
        contentId: attachment.contentId,
      });
    });

    this.currentEmail = new EmailClass({ ...email, attachments: transformedAttachments });

    await this.convertCidToContent(
      this.currentEmail.bodyHtml,
      this.currentEmail.attachments,
      this.currentEmail.storageId,
    )
      .then(() => {
        this.updateIframe();
        this.mailReviewService.setClassifierEmail(this.currentEmail);
        this.selectedMailService.selectMail(this.currentEmail);
      })
      .catch((error) => {
        console.error('Error converting CID to content:', error);
        this.mailReviewService.setNavigationButtonsDisable(false);
      });
  }

  async convertCidToContent(html: string, attachments: Attachment[], messageHandlerId: string): Promise<string> {
    if (!html) return html;

    const regex = /<img[^>]*?src=["']cid:([\w@.\-]+)["'][^>]*?>/g;

    const matches = [...html.matchAll(regex)];

    for (const match of matches) {
      const fullMatch = match[0];
      const cid = match[1];

      const newRef = await this.loadCidAndGetImage(cid, attachments, messageHandlerId);

      if (!newRef) {
        console.warn(`No se encontró una referencia válida para CID: ${cid}`);
        continue;
      }

      const newImgTag = fullMatch.replace(
        /src=["']cid:[\w@.\-]+["']/,
        `src="${newRef}" data-cid="${cid}" data-manipulated="true"`,
      );

      html = html.replace(fullMatch, newImgTag);
    }

    this.currentEmail.bodyHtml = html;
  }

  async loadCidAndGetImage(cid: string, attachments: Attachment[], messageHandlerId: string): Promise<string> {
    for (const attachment of attachments) {
      if (cid === attachment.contentId) {
        try {
          const content = await this.messageAttachmentService.getContentFile(
            attachment.filename,
            attachment.isSafe,
            messageHandlerId,
          );
          return content;
        } catch (error) {
          console.error('Error en la llamada a la API:', error);
        }
      }
    }
    console.warn(`No se encontró contenido para CID: ${cid}`);
    return '';
  }

  removeDocType() {
    this.currentEmail.bodyHtml = this.currentEmail.bodyHtml.replace('"-//W3C//DTD HTML 4.01//EN"', '');
  }

  private setIframeReady(iframe: ElementRef, html: string): void {
    const win: Window = iframe.nativeElement.contentWindow;

    const doc: Document = win?.document;

    if (doc) {
      doc.open();
      doc.write(html);
      doc.close();
      this.iframe.nativeElement.style.setProperty('display', 'inherit');
      this.iframe.nativeElement.style.setProperty(
        'height',
        this.iframe.nativeElement.contentWindow.document.body.scrollHeight + 'px',
      );
      this.iframe.nativeElement.style.setProperty('width', '95%');

      const htmlElement: HTMLIFrameElement = document.getElementById('emailIframe') as HTMLIFrameElement;
      const htmlIframe = htmlElement.contentWindow;
      if (htmlIframe) {
        const iframeDoc = htmlIframe.document;

        const style = iframeDoc.createElement('style');
        style.innerHTML = `
          a {
            color: #4A09B4 !important;
          }
        `;
        iframeDoc.head.appendChild(style);

        htmlIframe.document.querySelectorAll('*').forEach((n) => {
          if (n.tagName == 'A') {
            n.setAttribute('target', '_blank');
          }
        });
      }
    }
  }
}
